import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  IntegrationEntry, SavedCredential, credentialRemovalApi, deleteCredentialApi, listIntegrations,
} from '../../api/admin';
import { Modal, RowMenu, TableCard } from '../../components/fs';
import CredentialWizard from './CredentialWizard';
import { BrandTile } from './workflow/BrandIcon';
import { useToast } from '../../components/Toast';
import { PHONE, useMediaQuery } from '../../shared/media';

/**
 * Settings → Integrations (R10, screen 30).
 *
 * The accounts your workflows deliver to, in the design's table: the service
 * (its own mark, its category), whether it is connected, which account or
 * workspace, and what uses it. Every service in the catalogue is here, in two
 * honest sections:
 *
 *   * the ones that HOLD ACCOUNT CREDENTIALS — a token and the fields that
 *     identify the service, entered once and used by every form;
 *   * the ones that HOLD NOTHING, listed with the reason rather than left out.
 *     A Slack webhook URL *is* a channel and a Zap URL *is* a Zap, so the
 *     "credential" is itself the destination and belongs on the connection
 *     that points at it.
 *
 * Connecting is pasted tokens through the CredentialWizard (D10). HOW a
 * service connects is one seam, `connect()` below, keyed on the catalogue's
 * `connectMethod` — 'token' today, 'oauth' reserved — so a provider sign-in
 * can be added later without touching this layout. The card says "Connect"
 * and "Disconnect"; token wording lives inside the wizard.
 *
 * Several workspaces per service: each saved credential is its own line with
 * its own ⋯ (Replace token…, Disconnect…), so the second one is reachable —
 * R0 found Replace and Remove only ever acted on the first.
 *
 * Owners and Admins only (Q6). A Member with Edit on a form can CHOOSE a saved
 * credential in a workflow; they can never read or change one, and this page
 * is absent for them rather than disabled.
 */

type Filter = 'all' | 'connected' | 'missing';

type Editor =
  | { kind: 'add'; provider?: string }
  | { kind: 'replace'; entry: IntegrationEntry; credential: SavedCredential }
  | { kind: 'remove'; entry: IntegrationEntry; credential: SavedCredential }
  | { kind: 'using'; entry: IntegrationEntry }
  | null;

/** Connected · Needs attention (the last delivery through it was refused) · Not connected. */
export function integrationStatus(e: IntegrationEntry): 'connected' | 'attention' | 'none' {
  if (!e.credentials.length) return 'none';
  return e.usage.lastFailure ? 'attention' : 'connected';
}
const STATUS_WORD = { connected: 'Connected', attention: 'Needs attention', none: 'Not connected' } as const;
const STATUS_CLASS = { connected: 'ok', attention: 'warn', none: 'off' } as const;

export default function IntegrationCredentials() {
  const [entries, setEntries] = useState<IntegrationEntry[] | null>(null);
  const [error, setError] = useState('');
  const toast = useToast();
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [editor, setEditor] = useState<Editor>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const phone = useMediaQuery(PHONE);

  const load = () => listIntegrations()
    .then((r) => setEntries(r.integrations))
    .catch((e) => setError(e.message));

  useEffect(() => { load(); }, []);

  const { holders, passthroughs, counts } = useMemo(() => {
    const all = entries || [];
    const q = search.trim().toLowerCase();
    const match = (e: IntegrationEntry) => !q || e.title.toLowerCase().includes(q) || e.blurb.toLowerCase().includes(q)
      || e.category.toLowerCase().includes(q);
    const holds = all.filter((e) => e.fields);
    return {
      holders: holds.filter(match).filter((e) => (
        filter === 'all' ? true
          : filter === 'connected' ? e.credentials.length > 0
            : e.credentials.length === 0
      )),
      // The pass-throughs are never filtered away by "Connected" / "Not
      // connected": neither word applies to them.
      passthroughs: all.filter((e) => !e.fields).filter(match),
      counts: {
        all: all.length,
        connected: holds.filter((e) => e.credentials.length).length,
        missing: holds.filter((e) => !e.credentials.length).length,
        holders: holds.length,
        passthroughs: all.length - holds.length,
      },
    };
  }, [entries, filter, search]);

  /**
   * THE seam (§5). A token service opens the CredentialWizard — to add, or
   * to replace one saved credential. An 'oauth' service would open the
   * provider's sign-in here instead; none exists yet, so it falls through to
   * the wizard rather than offering a door that does not open.
   */
  function connect(entry: IntegrationEntry | null, replacing?: SavedCredential) {
    switch (entry?.connectMethod ?? 'token') {
      case 'oauth':
      case 'token':
      default:
        if (entry && replacing) setEditor({ kind: 'replace', entry, credential: replacing });
        else setEditor({ kind: 'add', provider: entry?.type });
    }
  }

  async function remove(id: number) {
    setError('');
    try { await deleteCredentialApi(id); setEditor(null); toast('Disconnected.'); load(); }
    catch (e: any) { setError(e.message); }
  }

  if (!entries) return <div className="fs-card pad st-col">{error ? <div className="ad-err">{error}</div> : 'Loading…'}</div>;

  /** One saved credential: what it is, and its own ⋯ (Replace, Disconnect). */
  const credentialLine = (e: IntegrationEntry, c: SavedCredential) => {
    const key = `${e.type}:${c.id}`;
    return (
      <div className="ic-cred" key={c.id}>
        <span className="ic-cred-text">{describe(c)}</span>
        <RowMenu open={openMenu === key} onToggle={(o) => setOpenMenu(o ? key : null)} label={`${e.title} · ${describe(c)}`}>
          <button onClick={() => connect(e, c)}>Replace token…</button>
          <div className="rule" />
          <button className="danger" onClick={() => setEditor({ kind: 'remove', entry: e, credential: c })}>Disconnect…</button>
        </RowMenu>
      </div>
    );
  };

  const statusCell = (e: IntegrationEntry) => {
    const st = integrationStatus(e);
    return (
      <>
        <span className={`st-state ${STATUS_CLASS[st]}`}>{STATUS_WORD[st]}</span>
        {st === 'attention' && (
          <div className="fs-cell-sub warn">
            last delivery refused{e.usage.lastFailure!.error ? ` · ${short(e.usage.lastFailure!.error)}` : ''}
          </div>
        )}
      </>
    );
  };

  const usedByCell = (e: IntegrationEntry) => (e.usage.connections > 0 ? (
    <button type="button" className="fs-btn text ic-used" onClick={() => setEditor({ kind: 'using', entry: e })}>
      {e.usage.forms} {e.usage.forms === 1 ? 'form' : 'forms'}
      <span className="st-muted"> · {e.usage.connections} {e.usage.connections === 1 ? 'connection' : 'connections'}</span>
    </button>
  ) : <span className="st-muted">—</span>);

  const connectButton = (e: IntegrationEntry) => (e.credentials.length ? (
    <button type="button" className="fs-btn text" onClick={() => connect(e)}>Add another {workspaceWord(e.type)}</button>
  ) : (
    <button type="button" className="fs-btn sm" onClick={() => connect(e)}>Connect</button>
  ));

  const service = (e: IntegrationEntry) => (
    <div className="ic-service">
      <BrandTile type={e.type} fallback={e.icon} className="ic-mark" size={16} />
      <div className="ic-service-text">
        <b>{e.title}</b>
        <div className="fs-cell-sub">{CATEGORY_WORD[e.category] || e.category}</div>
      </div>
    </div>
  );

  return (
    <div className="st-stack">
      <div className="fs-page-head">
        <div className="text">
          <h1>Integrations</h1>
          <div className="fs-page-sub">
            Accounts your workflows deliver to. Connect once, use on any form.
            <br />
            {counts.connected} connected · {counts.missing} available · {counts.passthroughs} need nothing here
          </div>
        </div>
        <button className="fs-btn primary" onClick={() => connect(null)}>Connect a service</button>
      </div>

      {error && <div className="ad-err">{error}</div>}

      <div className="ic-toolbar">
        <div className="fs-seg">
          <button className={filter === 'all' ? 'on' : ''} onClick={() => setFilter('all')}>
            All <span className="count">{counts.all}</span>
          </button>
          <button className={filter === 'connected' ? 'on' : ''} onClick={() => setFilter('connected')}>
            Connected <span className="count">{counts.connected}</span>
          </button>
          <button className={filter === 'missing' ? 'on' : ''} onClick={() => setFilter('missing')}>
            Not connected <span className="count">{counts.missing}</span>
          </button>
        </div>
        <span className="spacer" />
        <input
          className="fs-input fs-w-220" placeholder="Search integrations…" aria-label="Search integrations"
          value={search} onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <TableCard
        title={`Holds account credentials · ${counts.holders}`}
        hint="Entered once. Every form on this account can use them; a value set on a connection always wins."
      >
        {!holders.length ? (
          <div className="st-card-body st-muted">Nothing matches that filter.</div>
        ) : phone ? (
          <div className="ic-cards">
            {holders.map((e) => (
              <div className="ic-card" key={e.type}>
                <div className="head">{service(e)}{connectButton(e)}</div>
                <div className="facts">{statusCell(e)}</div>
                {e.credentials.map((c) => credentialLine(e, c))}
                {e.usage.connections > 0 && <div className="facts">Used by {usedByCell(e)}</div>}
              </div>
            ))}
          </div>
        ) : (
          <table className="fs-table ic-table">
            <thead>
              <tr><th>Service</th><th>Status</th><th>Account</th><th>Used by</th><th className="fs-row-actions" /></tr>
            </thead>
            <tbody>
              {holders.map((e) => (
                <tr key={e.type}>
                  <td>{service(e)}</td>
                  <td>{statusCell(e)}</td>
                  <td>
                    {e.credentials.length
                      ? e.credentials.map((c) => credentialLine(e, c))
                      : <span className="st-muted">{(e.fields || []).map((f) => f.label).join(' · ')}</span>}
                  </td>
                  <td>{usedByCell(e)}</td>
                  <td className="fs-row-actions">{connectButton(e)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </TableCard>

      <TableCard
        title={`Nothing to hold here · ${counts.passthroughs}`}
        hint="The destination URL is the setting, so it belongs to the connection that points at that channel."
        footer={(
          <span>
            Owners and Admins only. A Member with Edit on a form can choose a saved credential
            in a workflow, never read or change one.{' '}
            <Link to="/admin/settings/users">Who can do what →</Link>
            {' · '}
            A connection that carries its own token keeps using it.
          </span>
        )}
      >
        {phone ? (
          <div className="ic-cards">
            {passthroughs.map((e) => (
              <div className="ic-card quiet" key={e.type}>
                <div className="head">{service(e)}</div>
                <div className="facts">{e.blurb}</div>
                <div className="facts st-faint">{e.holdsNothing}</div>
              </div>
            ))}
          </div>
        ) : (
          <table className="fs-table ic-table">
            <thead><tr><th>Service</th><th>What it does</th><th>Where it is set</th></tr></thead>
            <tbody>
              {passthroughs.map((e) => (
                <tr key={e.type}>
                  <td>{service(e)}</td>
                  <td className="st-muted">{e.blurb}</td>
                  <td className="st-muted">{e.holdsNothing}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </TableCard>

      {editor?.kind === 'add' && (
        <CredentialWizard
          entries={entries}
          provider={editor.provider}
          onClose={() => setEditor(null)}
          onSaved={(name) => { toast(`${name} is connected.`); load(); }}
        />
      )}
      {editor?.kind === 'replace' && (
        <CredentialWizard
          entries={entries}
          provider={editor.entry.type}
          replacing={editor.credential}
          onClose={() => setEditor(null)}
          onSaved={() => { toast(`${editor.entry.title} updated.`); load(); }}
        />
      )}
      {editor?.kind === 'remove' && (
        <RemoveCredentialModal
          entry={editor.entry} credential={editor.credential}
          onClose={() => setEditor(null)}
          onConfirm={() => remove(editor.credential.id)}
        />
      )}
      {editor?.kind === 'using' && (
        <UsingModal entry={editor.entry} onClose={() => setEditor(null)} />
      )}
    </div>
  );
}

/** The catalogue's category, as the Service column's second line says it. */
const CATEGORY_WORD: Record<string, string> = {
  generic: 'Built in', project: 'Project management', crm: 'CRM', helpdesk: 'Help desk',
  chat: 'Chat', data: 'Spreadsheets & data', automation: 'Automation', workflow: 'Workflow',
};

/** The non-secret half of a saved credential is the useful label. */
function describe(c: SavedCredential): string {
  const shown = Object.entries(c.config || {})
    .filter(([, v]) => v !== null && v !== undefined && String(v).trim())
    .map(([, v]) => String(v));
  const when = c.updatedAt ? ` · updated ${new Date(c.updatedAt).toLocaleDateString()}` : '';
  return (shown[0] || c.label || 'saved') + when;
}

const short = (s: string) => (s.length > 60 ? `${s.slice(0, 57)}…` : s);

/** "Add another workspace" reads better than "add another credential". */
function workspaceWord(type: string): string {
  if (type === 'monday') return 'workspace';
  if (type === 'jira') return 'site';
  if (type === 'zendesk' || type === 'freshdesk') return 'domain';
  return 'account';
}

/**
 * Removing a credential — the rule-5 dialog it should always have been.
 *
 * The count is computed rather than warned about in the abstract: connections
 * that carry their own token are unaffected and are not counted, because a
 * value set on a connection always wins.
 */
function RemoveCredentialModal({ entry, credential, onClose, onConfirm }: {
  entry: IntegrationEntry;
  credential: SavedCredential;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const [info, setInfo] = useState<{ connections: number; forms: number } | null>(null);
  useEffect(() => { credentialRemovalApi(credential.id).then(setInfo).catch(() => undefined); }, [credential.id]);

  return (
    <Modal
      title={<>Disconnect <b>{entry.title}</b>{credential.label ? <> · {credential.label}</> : null}?</>} narrow onClose={onClose}
      footer={<>
        <button className="fs-btn danger" onClick={onConfirm}>Disconnect</button>
        <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
      </>}
    >
      <div className="fs-consequence warn">
        {info ? (
          info.connections ? (
            <>
              <b>
                {info.connections} {info.connections === 1 ? 'connection' : 'connections'} across{' '}
                {info.forms} {info.forms === 1 ? 'form' : 'forms'} will stop delivering.
              </b>{' '}
              They were relying on these credentials. Connections that carry their own token
              are unaffected — a value set on a connection always wins.
            </>
          ) : (
            <>Nothing is using these credentials yet, so nothing will stop working.</>
          )
        ) : 'Working out what relies on these…'}
      </div>
      <div className="fs-cell-sub" style={{ marginTop: 12 }}>
        The token is deleted here, not revoked at {entry.title}. Revoke it there too if it has leaked.
      </div>
    </Modal>
  );
}

function UsingModal({ entry, onClose }: { entry: IntegrationEntry; onClose: () => void }) {
  return (
    <Modal
      title={<>What uses the <b>{entry.title}</b> credentials</>} narrow onClose={onClose}
      footer={<button className="fs-btn neutral" onClick={onClose}>Close</button>}
    >
      <div className="fs-consequence">
        <b>
          {entry.usage.connections} {entry.usage.connections === 1 ? 'connection' : 'connections'} across{' '}
          {entry.usage.forms} {entry.usage.forms === 1 ? 'form' : 'forms'}.
        </b>{' '}
        A connection uses these whenever it does not carry a value of its own.
      </div>
      <div className="fs-cell-sub" style={{ marginTop: 12 }}>
        Each form's connections live on its Workflow tab.{' '}
        <Link to="/admin" onClick={onClose}>Browse forms →</Link>
      </div>
    </Modal>
  );
}
